import { Component } from '@theme/component';
import { morph } from '@theme/morph';
// VariantUpdateEvent is used for JSDoc type annotations
// eslint-disable-next-line no-unused-vars
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { debounce, requestIdleCallback } from '@theme/utilities';
import {
  MIN_GEOCODE_LENGTH,
  SEARCH_DEBOUNCE_MS,
  geocodePlace,
  hasPostalPrefix,
  locateShopper,
  showRowsMatching,
  toPostalPrefix,
} from '@theme/pickup-search';
import {
  MILES_TO_KILOMETRES,
  PICKUP_RADIUS_MILES,
  isSelectedStoreNamed,
  normalizeStoreName,
  readSelectedStore,
  toCoordinate,
  writeSelectedStore,
  writeShopperLocation,
} from '@theme/pickup-store-storage';
import { fetchPickupVocabulary, matchStoreRecord } from '@theme/pickup-store-vocabulary';

/**
 * How far apart Shopify's and Uberall's coordinates for the same store can sit. Comfortably
 * above the few hundred metres they actually differ by, and far below the miles separating
 * any two different stores — the locator's distance helper rounds to 0.1, so this is 6 steps.
 */
const SAME_STORE_MILES = 0.6;

class LocalPickup extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;

  /**
   * In-flight or resolved Uberall store list, kept so only the first opened
   * accordion pays for the request.
   * @type {Promise<any[]> | null}
   */
  #storesRequest = null;

  /**
   * The store locator helpers, imported on demand: they're only needed once a shopper
   * expands a store's details, so every other PDP visit skips the module entirely.
   * @type {Promise<typeof import('./store-locator-helpers.js')> | null}
   */
  #helpers = null;

  /** Whether the list is limited to the pickup radius, which gates the "can't find a store
   * nearby" prompt. @type {boolean} */
  #radius = false;

  /** Whether that radius is centred on the shopper, which switches the count wording.
   * @type {boolean} */
  #nearMe = false;

  /** The pass that settles the rows against Constructor and Uberall. @type {Promise<void> | null} */
  #enrichment = null;

  /** Bumped per search, so a slow geocode can tell a later keystroke has overtaken it. */
  #searchToken = 0;

  /**
   * Hides the location button once the list already reflects where the shopper is.
   * @param {boolean} radius
   * @param {boolean} [fromShopper]
   */
  #setRadius(radius, fromShopper = false) {
    this.#radius = radius;
    this.#nearMe = radius && fromShopper;

    const button = this.querySelector('[data-use-my-location]');
    if (button instanceof HTMLElement) button.hidden = this.#nearMe;
  }

  /**
   * The market the shopper is buying in, which scopes the stored store: the drawer only ever
   * lists stores collectable here, so a selection made in another market is not ours to read.
   */
  get #market() {
    return this.dataset.country;
  }

  /** The currently selected store's row, or null when nothing is selected. */
  get #selectedRow() {
    return (
      this.querySelector('.pickup-location__radio:checked')?.closest('[data-store-id]') ?? null
    );
  }

  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest(`.shopify-section, dialog`);

    /** @type {EventListener} */
    const variantUpdated = (event) => {
      const variantEvent = /** @type {VariantUpdateEvent} */ (event);
      if (variantEvent.detail.data.newProduct) {
        this.dataset.productUrl = variantEvent.detail.data.newProduct.url;
      }

      const variantId = variantEvent.detail.resource ? variantEvent.detail.resource.id : null;
      const variantAvailable = variantEvent.detail.resource
        ? variantEvent.detail.resource.available
        : null;
      // While no size is selected the block shows a "select a size" prompt, so the first
      // selection must refetch even when it matches the variant we were rendered with.
      const awaitingSelection = this.dataset.hasSelectedVariant === 'false';
      if (awaitingSelection || variantId !== this.dataset.variantId) {
        if (variantId && variantAvailable) {
          this.removeAttribute('hidden');
          this.dataset.variantId = variantId;
          this.#fetchAvailability(variantId);
        } else {
          this.setAttribute('hidden', '');
        }
      }
    };

    closestSection?.addEventListener(ThemeEvents.variantUpdate, variantUpdated);

    this.disconnectedCallback = () => {
      closestSection?.removeEventListener(ThemeEvents.variantUpdate, variantUpdated);
    };

    this.#applyStoredSelection();
    this.#hydrate();
  }

  /**
   * Selects the clicked store row. Clicks on the details accordion or the phone link
   * keep their own behavior instead of selecting.
   * @param {Event} event - The click event from a store row.
   */
  selectStore = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('details, a')) return;

    const radio = target.closest('[data-store-id]')?.querySelector('.pickup-location__radio');
    // Already the selected store: nothing to change.
    if (!(radio instanceof HTMLInputElement) || radio.checked) return;

    radio.checked = true;
  };

  /**
   * Persists the selected store, floats it to the top of the list and closes the drawer.
   */
  confirmStore = () => {
    const row = this.#selectedRow;
    const radio = row?.querySelector('.pickup-location__radio');
    if (!(row instanceof HTMLElement) || !(radio instanceof HTMLInputElement)) return;

    this.#chooseStore(row, radio);

    const dialog = /** @type {(Element & { closeDialog?: () => void }) | null} */ (
      this.querySelector('dialog-component')
    );
    dialog?.closeDialog?.();
  };

  /**
   * Makes a row's store the shopper's: checked in the list, stored, and named on the
   * collapsed trigger. Shared by the drawer's confirm button and the nearest-store pick,
   * so a store chosen either way is recorded the same.
   * @param {HTMLElement} row - The store's row.
   * @param {HTMLInputElement} radio - That row's radio.
   */
  #chooseStore(row, radio) {
    radio.checked = true;

    // Name and coordinates ride along: the collection filter knows stores by name only.
    writeSelectedStore(this.#market, {
      id: radio.value,
      name: row.dataset.storeName,
      uberallId: row.dataset.uberallId,
      lat: toCoordinate(row.dataset.storeLat),
      lng: toCoordinate(row.dataset.storeLng),
    });

    this.#moveSelectedStoreFirst(row);
    this.#updateSelectionSummary(row);
  }

  /**
   * Mirrors the selected store into the collapsed trigger: an availability message
   * replaces the prompt text, and the store's name becomes the button label. Both come
   * from server-rendered strings, so wording and localization stay in Liquid.
   * @param {HTMLElement} row - The selected store's row.
   * @param {string} [name] - The name to label the trigger with, for callers that know one
   *   the row doesn't yet carry — the row starts every render on Shopify's spelling.
   */
  #updateSelectionSummary(row, name) {
    const isAvailable = row.dataset.storeAvailable === 'true';
    const availability =
      (isAvailable ? this.dataset.summaryAvailable : this.dataset.summaryUnavailable) ?? '';
    const storeName = name || row.dataset.storeName || '';

    const availabilityTarget = this.querySelector('[data-selected-store-availability]');
    const nameTarget = this.querySelector('[data-selected-store-name]');

    if (availabilityTarget && availability) availabilityTarget.textContent = availability;
    if (nameTarget && storeName) nameTarget.textContent = storeName;

    // Server-rendered hidden: the pickup note only makes sense once there's a store
    // that can actually fulfill the pick up.
    const note = this.querySelector('[data-selected-store-note]');
    if (note instanceof HTMLElement) note.hidden = !isAvailable;

    // The pin only marks the prompt state — the availability dot takes over from here.
    const promptIcon = this.querySelector('[data-pickup-prompt-icon]');
    if (promptIcon instanceof HTMLElement) promptIcon.hidden = true;

    // Same dot as the drawer rows, coloured from the store's availability.
    const dot = this.querySelector('[data-selected-store-dot]');
    if (!(dot instanceof HTMLElement)) return;

    dot.classList.toggle('pickup-location__availability-icon--available', isAvailable);
    dot.classList.toggle('pickup-location__availability-icon--unavailable', !isAvailable);
    dot.hidden = false;
  }

  /**
   * Restores the stored store selection and floats it to the top of the list. Runs on
   * connect and after every re-render, since the server markup knows nothing about it.
   */
  #applyStoredSelection() {
    const stored = readSelectedStore(this.#market);
    if (!stored) return;

    this.#nameStoreInSizePrompt(stored);

    const row = this.#findStoredRow(stored);
    if (!row) return;

    const radio = row.querySelector('.pickup-location__radio');
    if (!(radio instanceof HTMLInputElement)) return;

    radio.checked = true;

    this.#moveSelectedStoreFirst(row);

    // Labelled from the stored name, not the row's: the row is back on Shopify's spelling
    // on every render, and #enrichFromUberall only reaches its Uberall one an idle callback
    // and a fetch later. Taking the row's name here and correcting it there is what made
    // the trigger flip between the two spellings on load and after every variant change.
    // The stored name is the one the drawer settled on, so both now agree from the start.
    this.#updateSelectionSummary(row, stored.name);
  }

  /**
   * Names the stored store in the select-a-size prompt. That prompt renders instead of the
   * store list, so there is no row to read a name off — the stored record is the only thing
   * that knows where the shopper would be collecting from before a size narrows it down.
   * A store the shopper has picked but that turns out not to stock the size is still the
   * store they'd be checking, so the prompt names it either way; what it actually has is
   * the answer the size selection goes on to give.
   * @param {import('./pickup-store-storage.js').SelectedStore} stored - The stored store.
   */
  #nameStoreInSizePrompt(stored) {
    if (this.dataset.hasSelectedVariant !== 'false' || !stored.name) return;

    const prompt = this.querySelector('[data-select-size-prompt]');
    const template = this.dataset.selectSizeAtStore;
    if (!(prompt instanceof HTMLElement) || !template) return;

    const marker = '{{ store }}';
    const markerIndex = template.indexOf(marker);

    if (markerIndex === -1) {
      prompt.textContent = template;
      return;
    }

    const before = template.slice(0, markerIndex);
    const after = template.slice(markerIndex + marker.length);
    const storeButton = document.createElement('span');

    storeButton.className = 'pickup-location__prompt-store-button';
    storeButton.textContent = stored.name;
    prompt.replaceChildren(before, storeButton, after);
  }

  /**
   * Finds a stored store's row. The location id matches selections made here; the Uberall id
   * is what a selection made on a collection page shares with this list, since that page
   * knows stores only as Uberall records. Names are the last resort: the two systems don't
   * always spell a store the same way, so a name match can only confirm, never rule out.
   * @param {import('./pickup-store-storage.js').SelectedStore} stored - The stored store.
   * @returns {HTMLElement | null} The store's row, or null when it isn't in this list.
   */
  #findStoredRow(stored) {
    if (stored.id) {
      const radio = this.querySelector(`.pickup-location__radio[value="${CSS.escape(stored.id)}"]`);
      const row = radio instanceof HTMLInputElement ? radio.closest('[data-store-id]') : null;
      if (row instanceof HTMLElement) return row;
    }

    if (stored.uberallId) {
      const row = this.querySelector(
        `[data-store-id][data-uberall-id="${CSS.escape(stored.uberallId)}"]`,
      );
      if (row instanceof HTMLElement) return row;
    }

    for (const row of this.querySelectorAll('[data-store-id]')) {
      if (row instanceof HTMLElement && isSelectedStoreNamed(stored, row.dataset.storeName)) {
        return row;
      }
    }

    return null;
  }

  /**
   * Settles the rows the server just rendered: Uberall's names and coordinates first, then
   * the sort around a remembered location, which can only place a row inside the radius once
   * that row has coordinates. Deferred to idle because a closed drawer's order isn't visible
   * yet, and the locator helpers are lazily imported by design.
   */
  #hydrate() {
    requestIdleCallback(async () => {
      await this.#enrichFromUberall();
      await this.#applyShopperLocation();
    });
  }

  /**
   * Settles the rows the server rendered against the two systems the drawer answers to.
   *
   * Constructor first, because its pickup facet is the list of stores that can actually be
   * collected from: a location missing from it can never be filtered to on a collection page,
   * so offering it here would let a shopper choose a store that then returns nothing. Its
   * spelling is taken too, which is what makes a store chosen here the same store there.
   *
   * Uberall second, for the coordinates Shopify is missing. Shopify returns none for a
   * location whose address it hasn't verified, and a row without them can't be shown to sit
   * inside the pickup radius — so "use my current location" was hiding every store but the
   * one Shopify had geocoded. Uberall has coordinates for all of them; they already place
   * every pin on the store locator's map. Nothing else is read from it here: the names it
   * keeps are its own ("Royalmount" for the store Shopify and Constructor both call Montreal)
   * and taking them is what used to break the collection page's filter.
   *
   * Runs against the store list this drawer fetches for the accordion's hours anyway, so the
   * rows paint with Shopify's data and settle rather than holding up the block.
   * @returns {Promise<void>} Resolves once the rows are settled.
   */
  #enrichFromUberall() {
    this.#enrichment = (async () => {
      try {
        const [vocabulary, helpers, stores] = await Promise.all([
          fetchPickupVocabulary(),
          this.#loadHelpers(),
          this.#fetchStores(),
        ]);

        this.#applyVocabulary(vocabulary);

        for (const row of this.querySelectorAll('[data-store-id]')) {
          if (!(row instanceof HTMLElement)) continue;

          const store = this.#findStore(stores, row, helpers.calculateDistance);
          if (store) this.#applyStoreCoordinates(row, store);
        }

        // The row count and the "no results" note both describe a list this pass has just
        // taken stores out of.
        this.#updateStoreCount();

        // Written from the old name by #applyStoredSelection, long before this resolved.
        const selected = this.#selectedRow;
        if (selected instanceof HTMLElement) {
          this.#updateSelectionSummary(selected);
          this.#storeSelectedName(selected);
        }
      } catch {
        // Nothing resolved: every row keeps the name and coordinates Liquid gave it.
      }
    })();

    return this.#enrichment;
  }

  /**
   * Drops the rows Constructor has no store for and relabels the rest with its spelling.
   *
   * Fails open on an empty list, which means either that the request failed or that this
   * market's index carries no pickup facet at all. Neither is a reason to strip the drawer of
   * stores Shopify says can fulfil the order — a shopper who can't filter by a store on a
   * collection page can still collect from it here.
   * @param {string[]} vocabulary - The stores Constructor can filter on.
   */
  #applyVocabulary(vocabulary) {
    if (!vocabulary.length) return;

    /** @type {HTMLElement[]} */
    const rows = [];
    for (const row of this.querySelectorAll('[data-store-id]')) {
      if (row instanceof HTMLElement) rows.push(row);
    }

    /** @type {Set<HTMLElement>} */
    const listed = new Set();

    for (const value of vocabulary) {
      const row = matchStoreRecord(
        value,
        rows,
        (candidate) => candidate.dataset.storeName,
        (candidate) => candidate.dataset.storeCity,
      );
      // Already claimed by an earlier value: ambiguous, and the first match is no better a
      // guess than the second, so this value simply goes unrepresented.
      if (!row || listed.has(row)) continue;

      listed.add(row);
      if (normalizeStoreName(row.dataset.storeName) !== normalizeStoreName(value)) {
        this.#renameRow(row, value);
      }
    }

    // Removed rather than hidden: `hidden` is what the radius and the search both toggle, so
    // a store held back here would come back the moment either of them ran.
    for (const row of rows) {
      if (!listed.has(row)) row.remove();
    }
  }

  /**
   * Fills in a row's coordinates from its Uberall record, where Shopify left them empty.
   * Only there: where Shopify has coordinates, they're part of what matched this row to this
   * record in the first place, and overwriting them would undo that.
   * @param {HTMLElement} row - The store's row.
   * @param {any} store - The matching Uberall location.
   */
  #applyStoreCoordinates(row, store) {
    if (row.dataset.storeLat && row.dataset.storeLng) return;

    const lat = Number(store?.lat);
    const lng = Number(store?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    row.dataset.storeLat = String(lat);
    row.dataset.storeLng = String(lng);
  }

  /**
   * Renames one row, in the dataset as well as on screen: the dataset name is what the
   * collapsed summary shows, what a confirmed selection is stored under, and what the
   * collection page then filters on.
   * @param {HTMLElement} row - The store's row.
   * @param {string} name - The store's name as Constructor's pickup facet spells it.
   */
  #renameRow(row, name) {
    // Kept so #findStore can still reach the Uberall record by name: that match reads
    // Shopify's spelling, and this is the last moment it exists on the row.
    row.dataset.storeSourceName ??= row.dataset.storeName ?? '';

    const label = row.querySelector('.pickup-location__text-bold');

    // The name is the label's leading text node, ahead of the distance span. Its trailing
    // space is load-bearing: the radio's accessible name concatenates the two without
    // inserting whitespace, and would otherwise announce "Royalmount3.7 mi".
    if (label?.firstChild instanceof Text) label.firstChild.data = `${name} `;

    row.dataset.storeName = name;

    // Prepended rather than substituted: the shopper can now see this name, so searching it
    // has to find the row. Liquid's text stays searchable behind it, which costs nothing.
    const searchText = row.dataset.storeSearchText;
    if (searchText) row.dataset.storeSearchText = `${name.toLowerCase()} ${searchText}`;
  }

  /**
   * Corrects the stored store's name to the one the relabel settled on. A shopper who
   * confirms a store before this pass lands stores Shopify's spelling for it, which every
   * later render would then show until the relabel corrected it again — the same flip
   * #applyStoredSelection now avoids, just persisted. Only the name moves.
   * @param {HTMLElement} row - The row of the store the shopper has selected.
   */
  #storeSelectedName(row) {
    const stored = readSelectedStore(this.#market);
    if (!stored) return;

    // Only the stored store's own row: a row clicked in the drawer but never confirmed is
    // `:checked` too, and must not rewrite the name of the store they actually chose.
    if (this.#findStoredRow(stored) !== row) return;

    const name = row.dataset.storeName;
    if (!name || isSelectedStoreNamed(stored, name)) return;

    writeSelectedStore(this.#market, { ...stored, name });
  }

  /**
   * Moves the selected store's row to the top of the list, above the in-stock-first
   * order the server renders.
   * @param {HTMLElement} row - The selected store's row.
   */
  #moveSelectedStoreFirst(row) {
    const list = row.parentElement;

    if (!list) return;

    // Before the first row rather than the list's first child, so the count stays on top.
    const firstRow = list.querySelector('[data-store-id]');
    if (firstRow === row) return;

    list.insertBefore(row, firstRow);
  }

  /**
   * Bound from the dialog's search input via `on:input`. Debounced because a query naming no
   * store has to be geocoded, and a request per keystroke is both slow and billed.
   * @param {Event} event
   */
  filterStores = debounce(
    /** @param {Event} event */
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      void this.#search(input.value);
    },
    SEARCH_DEBOUNCE_MS,
  );

  /**
   * A postal code, whole or partial, is answered with the stores inside the pickup radius of
   * it; a name/street/city query shows those stores; anything else is geocoded to a radius.
   * Search shares the radius's `hidden`, so it replaces that view rather than intersecting it.
   * @param {string} query - What the shopper typed.
   */
  async #search(query) {
    const token = ++this.#searchToken;
    const normalized = query.trim().toLowerCase();

    if (!normalized) {
      this.clearSearch();
      return;
    }

    // Before the text match: a code is the front of a store's code, not a word in its
    // address. Google is no help with a partial one — it reads "1001" as 01001, Agawam MA.
    const prefix = toPostalPrefix(normalized);
    const anchors = prefix ? this.#rowsWithPostalPrefix(prefix) : [];

    if (anchors.length === 0 && showRowsMatching(this.#rows, normalized) > 0) {
      this.#dropRadius();
      return;
    }

    // Concurrent: neither reads the other's result. Geocoding only where no store's code
    // matched; the enrichment always, since the anchors need coordinates too.
    const [geocoded] = await Promise.all([
      anchors.length === 0 && normalized.length >= MIN_GEOCODE_LENGTH
        ? geocodePlace(query, this.dataset.mapsKey, this.dataset.country)
        : null,
      this.#ensureEnriched(),
    ]);

    const { sortStoresByNearestOrigin } = await this.#loadHelpers();

    // A later keystroke owns the list now, and has already rendered its own answer.
    if (token !== this.#searchToken) return;

    /** @type {{lat: number, lng: number}[]} */
    let origins = [];
    if (anchors.length > 0) origins = this.#rowOrigins(anchors);
    else if (geocoded) origins = [geocoded];

    // Every row is hidden already: the query matched no store and named no place.
    if (origins.length === 0) {
      this.#dropRadius();
      return;
    }

    this.#sortRowsByDistance(origins, sortStoresByNearestOrigin);
    this.#setRadius(true);
    this.#updateStoreCount();
  }

  /** Every store row, in document order. */
  get #rows() {
    return this.querySelectorAll('[data-store-id]');
  }

  /**
   * The rows a postal search draws its radius around.
   * @param {string} prefix - A prefix from `toPostalPrefix`.
   * @returns {HTMLElement[]}
   */
  #rowsWithPostalPrefix(prefix) {
    /** @type {HTMLElement[]} */
    const rows = [];

    for (const row of this.#rows) {
      if (row instanceof HTMLElement && hasPostalPrefix(row.dataset.storeZip, prefix)) {
        rows.push(row);
      }
    }

    return rows;
  }

  /**
   * The coordinates of the given rows, skipping any neither system could place.
   * @param {HTMLElement[]} rows
   * @returns {{lat: number, lng: number}[]}
   */
  #rowOrigins(rows) {
    /** @type {{lat: number, lng: number}[]} */
    const origins = [];

    for (const row of rows) {
      const lat = Number(row.dataset.storeLat);
      const lng = Number(row.dataset.storeLng);
      // Empty attributes coerce to 0, which would anchor the search at 0°,0°.
      if (!row.dataset.storeLat || !row.dataset.storeLng) continue;
      if (Number.isFinite(lat) && Number.isFinite(lng)) origins.push({ lat, lng });
    }

    return origins;
  }

  /**
   * Empties the search box and drops the radius so the whole list shows again. The only way
   * back out of a list a search or the radius has narrowed, possibly to nothing.
   */
  clearSearch = () => {
    const input = this.querySelector('.pickup-location__search-input');
    if (input instanceof HTMLInputElement) input.value = '';

    // A pending or in-flight search would otherwise land on this list and narrow it again.
    this.#cancelSearch();

    for (const row of this.#rows) row.removeAttribute('hidden');

    // Distances and their nearest-first order stay: the list simply isn't limited to
    // what's near the shopper any more, so the count drops the "near you" wording.
    this.#dropRadius();
  };

  /** Drops a debounced search, and disowns any geocode already on its way back. */
  #cancelSearch() {
    this.filterStores.cancel();
    this.#searchToken += 1;
  }

  /** Leaves the radius view and re-counts whatever the list is left showing. */
  #dropRadius() {
    this.#setRadius(false);
    this.#updateStoreCount();
  }

  /**
   * The Uberall pass, started if nothing has yet. A row Shopify left without coordinates
   * can't be placed inside the radius until it lands.
   * @returns {Promise<void>}
   */
  #ensureEnriched() {
    return this.#enrichment ?? this.#enrichFromUberall();
  }

  /**
   * Sorts the list nearest-first from the browser's geolocation, reusing the store
   * locator's geolocation and distance helpers. Bound from the dialog's
   * "use my current location" button via `on:click`.
   * @param {Event} event - The click event from the location button.
   */
  useMyLocation = async (event) => {
    // `on:` handlers are dispatched from a single delegated listener on `document`, so
    // `currentTarget` is `document` here, never the button. `target` is the element that
    // carries the attribute, which the framework proxies in when the click lands on the
    // button's icon. Read synchronously: the event is stale once this function awaits.
    const target = event.target;
    const button = target instanceof Element ? target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = true;
    // The list is about to be re-sorted around the shopper; a search still on its way back
    // would land on top of that.
    this.#cancelSearch();

    try {
      const { getUserGeolocation, sortStoresByNearestOrigin } = await this.#loadHelpers();

      // Concurrent: a GPS fix says nothing about the Uberall list. Without the enrichment,
      // rows Shopify never geocoded drop out of the radius entirely.
      const [coordinates] = await Promise.all([getUserGeolocation(), this.#ensureEnriched()]);

      // Remembered from here on, so later visits skip straight to the sorted list.
      writeShopperLocation(coordinates);

      this.#sortRowsByDistance([coordinates], sortStoresByNearestOrigin);
      this.#setRadius(true, true);
      this.#updateStoreCount();
    } catch {
      // Denied, unsupported or timed out: the list keeps its server order.
    } finally {
      // Re-enabled even when #setRadius has just hidden it, so the button is usable
      // again the moment a search or "view all stores" brings it back.
      button.disabled = false;
    }
  };

  /**
   * Sorts by a remembered location, so a returning shopper gets nearest-first without
   * pressing the button again. Called from #hydrate, which has already waited for the
   * Uberall coordinates every row is measured from.
   * @returns {Promise<void>}
   */
  async #applyShopperLocation() {
    // No rows until a size is picked, and nothing to reorder — or choose from — without
    // them. Checked before locating, so a PDP still on its size prompt reads no GPS.
    if (!this.querySelector('[data-store-id]')) return;

    const coordinates = await locateShopper();
    if (!coordinates) return;

    try {
      const { sortStoresByNearestOrigin } = await this.#loadHelpers();

      this.#sortRowsByDistance([coordinates], sortStoresByNearestOrigin);
      this.#setRadius(true, true);
      this.#updateStoreCount();
      this.#chooseNearestStore();
    } catch {
      // Helpers unavailable: the list keeps its server order.
    }
  }

  /**
   * Picks the store at the top of the freshly sorted list for a shopper who has none.
   *
   * That row is the list's own answer to "where would you collect this from": nearest of the
   * stores holding the size, or simply nearest when none of them do, and never one outside
   * the pickup radius, since #sortRowsByDistance has already hidden those. Rows only exist
   * once a size is picked, so the first visit to a PDP settles this and every later surface
   * reads the stored result.
   *
   * Only ever fills a gap. A store the shopper chose themselves stands until they change it,
   * and this leaves the pickup filter's `enabled` flag unset, so a store picked for them
   * never silently filters a collection down — that stays something they turn on.
   */
  #chooseNearestStore() {
    if (readSelectedStore(this.#market)) return;

    // Document order, which the sort has just made distance order; hidden rows are the ones
    // beyond the radius or missing the coordinates to be placed inside it.
    const nearest = [...this.#rows].find((row) => !row.hasAttribute('hidden'));
    const radio = nearest?.querySelector('.pickup-location__radio');
    if (!(nearest instanceof HTMLElement) || !(radio instanceof HTMLInputElement)) return;

    this.#chooseStore(nearest, radio);
  }

  /**
   * Reorders the store rows: stores holding the variant first, nearest-first within that
   * group, then the rest in the same order, then the selected store pinned above all of
   * it. Everything outside the pickup radius is hidden. Rows without coordinates can't be
   * shown to be within it, so they're hidden too — an unverified address in admin takes a
   * store out of the located list entirely.
   * @param {{lat: number, lng: number}[]} origins - Points to measure from; each row is
   *   measured from whichever it is nearest.
   * @param {(stores: any[], origins: any[]) => any[]} sortStoresByNearestOrigin
   */
  #sortRowsByDistance(origins, sortStoresByNearestOrigin) {
    const rows = [...this.#rows];
    const list = rows[0]?.parentElement;
    if (!list) return;

    /** @type {{row: HTMLElement, lat: number, lng: number}[]} */
    const located = [];
    /** @type {Element[]} */
    const unlocated = [];

    for (const row of rows) {
      if (!(row instanceof HTMLElement)) continue;

      const lat = Number(row.dataset.storeLat);
      const lng = Number(row.dataset.storeLng);
      // Empty attributes coerce to 0, which would otherwise sort as 0°,0°.
      const hasCoordinates =
        row.dataset.storeLat &&
        row.dataset.storeLng &&
        Number.isFinite(lat) &&
        Number.isFinite(lng);

      if (hasCoordinates) located.push({ row, lat, lng });
      else unlocated.push(row);
    }

    // The helper spreads each entry into its result, so `row` and the distance it adds
    // both survive the sort.
    const ranked = sortStoresByNearestOrigin(located, origins);

    // Distance orders each group, but a store holding the variant outranks one that isn't
    // however much closer it is — the same in-stock-first grouping the server renders,
    // which sorting on distance alone would otherwise scramble.
    const hasVariant = (/** @type {{row: HTMLElement}} */ entry) =>
      entry.row.dataset.storeAvailable === 'true';
    const grouped = [...ranked.filter(hasVariant), ...ranked.filter((entry) => !hasVariant(entry))];

    const sorted = [...grouped.map(({ row }) => row), ...unlocated];

    // Captured before the moves: the rows shift, the trailing copy below them doesn't.
    const anchor = rows[rows.length - 1]?.nextElementSibling ?? null;
    for (const row of sorted) list.insertBefore(row, anchor);

    // A store the shopper has already chosen is filtered on distance like any other: a
    // list headed "stores near you" can't hold one that's hours away, and a stale
    // selection would otherwise be the only row left standing. The collapsed trigger
    // still names their choice, and "view all stores" brings the row back.
    for (const row of unlocated) row.setAttribute('hidden', '');

    for (const { row, distance } of ranked) {
      this.#renderDistance(row, distance);
      row.toggleAttribute('hidden', distance > PICKUP_RADIUS_MILES);
    }

    const selected = this.#selectedRow;

    // Last, because the grouping above reorders every row: a store the shopper has chosen
    // sits above both groups, matching the collapsed trigger that names it.
    if (selected instanceof HTMLElement) this.#moveSelectedStoreFirst(selected);
  }

  /**
   * Labels a row with its distance from the shopper, in the unit Liquid picked for their
   * market. Rows without coordinates never get here, so they stay unlabelled.
   * @param {Element} row - The store's row.
   * @param {number} miles - Distance in miles, as the locator helper returns it.
   */
  #renderDistance(row, miles) {
    const target = row.querySelector('[data-store-distance]');
    const template = this.dataset.tDistance;
    if (!(target instanceof HTMLElement) || !template || !Number.isFinite(miles)) return;

    const distance = this.dataset.distanceUnit === 'km' ? miles * MILES_TO_KILOMETRES : miles;

    // One decimal, matching the helper's own rounding of the miles value.
    target.textContent = template.replace('{{ distance }}', distance.toFixed(1));
    target.hidden = false;
  }

  /**
   * Rewrites the count and shows the "view all locations" prompt when stores are held back.
   * Both are server-rendered for the unfiltered list, so this only runs once something has
   * changed what's on show.
   */
  #updateStoreCount() {
    const total = this.#rows.length;
    const visible = this.querySelectorAll('[data-store-id]:not([hidden])').length;

    // Radius view only: it answers "can't find a store nearby?", which the full listing
    // that pressing it produces has not asked. Above the count guard, which can return early.
    const viewAll = this.querySelector('[data-view-all-locations]');
    if (viewAll instanceof HTMLElement) viewAll.hidden = !this.#radius || visible === total;

    // Only stands in for the list when the list has nothing to show — a narrowed list
    // that still holds a store speaks for itself.
    const noResults = this.querySelector('[data-no-results]');
    if (noResults instanceof HTMLElement) noResults.hidden = visible > 0;

    const target = this.querySelector('[data-store-count]');
    if (!(target instanceof HTMLElement)) return;

    const one = this.#nearMe ? this.dataset.countNearOne : this.dataset.countOne;
    const other = this.#nearMe ? this.dataset.countNearOther : this.dataset.countOther;
    const template = (visible === 1 ? one : other) ?? '';
    if (template) target.textContent = template.replace('{{ count }}', String(visible));
  }

  /**
   * Fills in a store's opening hours the first time its details accordion is opened.
   * Bound declaratively from the accordion's `on:toggle`.
   * @param {Event} event - The toggle event from a store details accordion.
   */
  loadStoreHours = async (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    const container = details.querySelector('[data-store-hours]');
    if (!(container instanceof HTMLElement) || container.dataset.loaded === 'true') return;

    const row = details.closest('[data-store-id]');
    if (!(row instanceof HTMLElement)) return;

    const [{ calculateDistance, formatOpeningHours, formatPhoneHref }, stores] = await Promise.all([
      this.#loadHelpers(),
      this.#fetchStores(),
    ]);

    const store = this.#findStore(stores, row, calculateDistance);

    // Before the hours guard below: a store with no hours can still have a phone.
    this.#renderStorePhone(details, store?.phone, formatPhoneHref);

    const hours = formatOpeningHours(store?.openingHours, this.#getDayNames());

    // Leave it unmarked when there's nothing to show so reopening can retry.
    if (!hours.length) return;

    container.dataset.loaded = 'true';
    container.replaceChildren(
      ...hours.map((/** @type {string} */ line) => {
        const paragraph = document.createElement('p');
        paragraph.textContent = line;
        return paragraph;
      }),
    );
  };

  /**
   * Renders a store's phone as a `tel:` link, matching the store locator's card markup.
   * Uberall is the maintained record, so Liquid renders this empty and hidden.
   * @param {HTMLDetailsElement} details - The store's details accordion.
   * @param {string | undefined} phone - The store's phone number from Uberall.
   * @param {(phone: string) => string} formatPhoneHref - Cleans the number for `tel:`.
   */
  #renderStorePhone(details, phone, formatPhoneHref) {
    const target = details.querySelector('[data-store-phone]');
    if (!(target instanceof HTMLElement) || !phone) return;

    const link = document.createElement('a');
    link.href = `tel:${formatPhoneHref(phone)}`;
    link.textContent = phone;

    target.replaceChildren(link);
    target.hidden = false;
  }

  /**
   * Loads the store locator helpers on first use, memoized.
   * @returns {Promise<typeof import('./store-locator-helpers.js')>} The helpers module.
   */
  #loadHelpers() {
    this.#helpers ??= import('@theme/store-locator-helpers');

    return this.#helpers;
  }

  /**
   * Fetches the Uberall store list, sharing the store locator's session cache.
   * Uberall's per-store endpoint is keyed by its own location id, which Shopify
   * pickup locations don't carry — hence the list plus a local match.
   * @returns {Promise<any[]>} The Uberall locations, or an empty array on failure.
   */
  #fetchStores() {
    if (this.#storesRequest) return this.#storesRequest;

    const storeKey = this.dataset.apiKey;
    if (!storeKey) return Promise.resolve([]);

    const request = this.#loadHelpers()
      .then(async (helpers) => {
        // The cache helper is untyped (`Object | null`); it stores the locations array.
        const cached = /** @type {any[] | null} */ (helpers.getCachedStores());
        if (cached) return cached;

        // Same field set the locator caches, so neither feature can serve the other a
        // payload that's missing fields it renders.
        const url = `${helpers.getApiEndpoint(storeKey)}${helpers.buildFieldMask(helpers.STORE_LIST_FIELDS)}`;
        const response = await fetch(url);

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const locations = (await response.json()).response?.locations ?? [];
        helpers.setCachedStores(locations);

        return locations;
      })
      .catch(() => {
        // Drop the memo so the next accordion open can retry.
        this.#storesRequest = null;
        return [];
      });

    this.#storesRequest = request;

    return request;
  }

  /**
   * Matches a pickup location to its Uberall record, on whichever key the two systems have
   * managed to agree about: the `uberall_id` metafield where it's set, then the postal code,
   * then how close the two records sit. Every key is optional in the data, so all are tried.
   * @param {any[]} stores - Uberall locations.
   * @param {HTMLElement} row - The pickup location's row.
   * @param {(lat1: number, lng1: number, lat2: number, lng2: number) => number} distance -
   *   The locator's distance helper, in miles.
   * @returns {any} The matching store, or undefined.
   */
  #findStore(stores, row, distance) {
    const normalize = (/** @type {unknown} */ value) =>
      String(value ?? '')
        .trim()
        .toLowerCase();

    const targetId = normalize(row.dataset.uberallId);
    if (targetId) {
      const match = stores.find((store) => normalize(store.id) === targetId);
      if (match) return match;
    }

    // Spaces dropped, not just collapsed: a Canadian postal code is as likely to be typed
    // "H4P0C9" in one system as "H4P 0C9" in the other, and no two zips differ by only a space.
    const zip = (/** @type {unknown} */ value) => normalize(value).replace(/\s+/g, '');

    const targetZip = zip(row.dataset.storeZip);
    if (targetZip) {
      const match = stores.find((store) => zip(store.zip) === targetZip);
      if (match) return match;
    }

    const nearest = this.#findNearestStore(stores, row, distance);
    if (nearest) return nearest;

    // Assumes Uberall only adds the brand prefix to the Shopify name, which holds for the
    // locations the two systems spell alike and fails for the ones they don't — hence the
    // metafield above. Left as a last resort for a location that's missing it.
    const targetName = normalize(row.dataset.storeSourceName || row.dataset.storeName);
    if (!targetName) return undefined;

    return stores.find((store) => normalize(store.name).includes(targetName));
  }

  /**
   * The Uberall record standing at the same address as a pickup location. Shopify's and
   * Uberall's own coordinates for one store land within a few hundred metres of each other,
   * while the nearest genuinely different store is miles off — even in Manhattan, where
   * Madison Avenue and Soho are four miles apart. So the cap below both finds the record and
   * rules out a near neighbour, for the stores where the id and the postal code disagree.
   * @param {any[]} stores - Uberall locations.
   * @param {HTMLElement} row - The pickup location's row.
   * @param {(lat1: number, lng1: number, lat2: number, lng2: number) => number} distance -
   *   The locator's distance helper, in miles.
   * @returns {any} The matching store, or undefined when nothing is close enough.
   */
  #findNearestStore(stores, row, distance) {
    const lat = Number(row.dataset.storeLat);
    const lng = Number(row.dataset.storeLng);
    // An unverified address in Shopify admin has neither, so there's nothing to measure from.
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;

    let closest;
    let closestMiles = Infinity;

    for (const store of stores) {
      const storeLat = Number(store?.lat);
      const storeLng = Number(store?.lng);
      if (!Number.isFinite(storeLat) || !Number.isFinite(storeLng)) continue;

      const miles = distance(lat, lng, storeLat, storeLng);
      if (miles < closestMiles) {
        closest = store;
        closestMiles = miles;
      }
    }

    return closestMiles <= SAME_STORE_MILES ? closest : undefined;
  }

  /**
   * Localized day names for the hours formatter. Index 0 is empty so the array lines up
   * with Uberall's dayOfWeek (1 = Monday), same as store-details.js's #getDayNames.
   * @returns {string[] | undefined} Day names, or undefined to use the formatter's default.
   */
  #getDayNames() {
    const days = this.dataset.tDays;

    return days ? ['', ...days.split(',')] : undefined;
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  /**
   * Fetches the availability of a variant.
   * @param {string} variantId - The ID of the variant to fetch availability for.
   */
  #fetchAvailability = (variantId) => {
    if (!variantId) return;

    const abortController = this.#createAbortController();

    const url = this.dataset.productUrl;
    fetch(`${url}?variant=${variantId}&section_id=${this.dataset.sectionId}`, {
      signal: abortController.signal,
    })
      .then((response) => response.text())
      .then((text) => {
        if (abortController.signal.aborted) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const wrapper = html.querySelector(`local-pickup[data-variant-id="${variantId}"]`);
        if (wrapper) {
          this.removeAttribute('hidden');
          morph(this, wrapper);
          // The fresh markup is back in server order with a server-rendered count, and
          // its location button comes back visible until #hydrate re-sorts. A search still
          // in flight was answering for rows this morph has just replaced.
          this.#setRadius(false);
          this.#cancelSearch();
          // The fresh markup carries no selection or distances, and is back to Shopify's
          // store names and coordinates, so all of it is reapplied after every morph. The
          // store list is memoized, so the relabel costs no second request.
          this.#applyStoredSelection();
          this.#hydrate();
        } else {
          this.setAttribute('hidden', '');
        }
      })
      .catch(() => {
        if (abortController.signal.aborted) return;
        this.setAttribute('hidden', '');
      });
  };
}

if (!customElements.get('local-pickup')) {
  customElements.define('local-pickup', LocalPickup);
}
